'use client';

import { c as _c } from "react/compiler-runtime";
import type { JSX, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { buildSealCheckPath, buildSealPath, buildSmoothPath, CONSENSUS_CHART_SHAPES, CONSENSUS_CHART_TARGET_Y, CONSENSUS_CHART_TIMING, CONSENSUS_CHART_VIEW_WIDTH, CONSENSUS_CHART_X, CONSENSUS_CHART_X_MAX, type ConsensusChartLineKey, canMeasureSvgPath, easeInOutCubic, estimatePolylineLength, samplePolylinePoint } from '@/components/support-mainnet/how-it-works/consensus-builds-chart-engine';
import { useHowItWorksOrchestrationActive, useHowItWorksPlaybackGeneration, useHowItWorksPlaybackInstanceKey, useHowItWorksStepShouldPlay } from '@/components/support-mainnet/how-it-works/how-it-works-step-playback-api';
import { cn } from '@/lib/ui/utils';
const LINE_KEYS: ConsensusChartLineKey[] = ['spike', 'mid', 'win'];
const LINE_PATHS = {
  spike: buildSmoothPath(CONSENSUS_CHART_X, CONSENSUS_CHART_SHAPES.spike),
  mid: buildSmoothPath(CONSENSUS_CHART_X, CONSENSUS_CHART_SHAPES.mid),
  win: buildSmoothPath(CONSENSUS_CHART_X, CONSENSUS_CHART_SHAPES.win)
} as const;
type LineElements = Record<ConsensusChartLineKey, {
  path: SVGPathElement;
  tip: SVGCircleElement;
  length: number;
  usesNativeMeasurement: boolean;
}>;
function setRevealFraction(elements: LineElements, key: ConsensusChartLineKey, fraction: number): void {
  const line = elements[key];
  line.path.style.strokeDashoffset = `${line.length * (1 - fraction)}`;
  const point = line.usesNativeMeasurement ? line.path.getPointAtLength(line.length * fraction) : samplePolylinePoint(CONSENSUS_CHART_X, CONSENSUS_CHART_SHAPES[key], fraction);
  line.tip.setAttribute('cx', `${point.x}`);
  line.tip.setAttribute('cy', `${point.y}`);
}
function dimLoserLines(elements: LineElements): void {
  elements.spike.path.classList.add('consensus-chart-line-loser');
  elements.spike.tip.classList.add('consensus-chart-line-loser');
  elements.mid.path.classList.add('consensus-chart-line-loser');
  elements.mid.tip.classList.add('consensus-chart-line-loser');
}
function resetChart(elements: LineElements): void {
  for (const key of LINE_KEYS) {
    setRevealFraction(elements, key, 0);
    elements[key].path.classList.remove('consensus-chart-line-loser');
    elements[key].tip.classList.remove('consensus-chart-line-loser');
    elements[key].tip.style.opacity = '1';
  }
}
function jumpToFinalState(elements: LineElements, setTargetLive: (live: boolean) => void, setLosersDimmed: (dimmed: boolean) => void, setSealVisible: (visible: boolean) => void, setHideWinTip: (hidden: boolean) => void): void {
  for (const key of LINE_KEYS) {
    setRevealFraction(elements, key, 1);
  }
  dimLoserLines(elements);
  elements.win.tip.style.opacity = '0';
  setTargetLive(true);
  setLosersDimmed(true);
  setHideWinTip(true);
  setSealVisible(true);
}
function bindLineElements(svg: SVGSVGElement): LineElements | null {
  const elements = {} as LineElements;
  let usesNativeMeasurement = true;
  for (const key of LINE_KEYS) {
    const path = svg.querySelector<SVGPathElement>(`#consensus-line-${key}`);
    const tip = svg.querySelector<SVGCircleElement>(`#consensus-tip-${key}`);
    if (path === null || tip === null) {
      return null;
    }
    if (!canMeasureSvgPath(path)) {
      usesNativeMeasurement = false;
    }
    const length = usesNativeMeasurement ? path.getTotalLength() : estimatePolylineLength(CONSENSUS_CHART_X, CONSENSUS_CHART_SHAPES[key]);
    path.style.strokeDasharray = `${length}`;
    elements[key] = {
      path,
      tip,
      length,
      usesNativeMeasurement
    };
  }
  if (!usesNativeMeasurement) {
    for (const key of LINE_KEYS) {
      elements[key].usesNativeMeasurement = false;
    }
  }
  return elements;
}
function useConsensusChartPlayback(rootRef, svgRef) {
  const $ = _c(15);
  const [targetLive, setTargetLive] = useState(false);
  const [losersDimmed, setLosersDimmed] = useState(false);
  const [sealVisible, setSealVisible] = useState(false);
  const [hideWinTip, setHideWinTip] = useState(false);
  const playedRef = useRef(false);
  const orchestrationActive = useHowItWorksOrchestrationActive();
  const shouldPlay = useHowItWorksStepShouldPlay(1);
  const playbackGeneration = useHowItWorksPlaybackGeneration(1);
  const playbackEnabled = orchestrationActive === false || shouldPlay;
  let t0;
  if ($[0] !== orchestrationActive || $[1] !== playbackEnabled || $[2] !== rootRef || $[3] !== svgRef) {
    t0 = () => {
      const root = rootRef.current;
      const svg = svgRef.current;
      if (root === null || svg === null) {
        return;
      }
      const elements = bindLineElements(svg);
      if (elements === null) {
        return;
      }
      playedRef.current = false;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const canAnimate = LINE_KEYS.every(key => elements[key].usesNativeMeasurement);
      let frameId = null;
      let dimTimer;
      let sealTimer;
      let crossedTarget = false;
      const cleanupPlayback = () => {
        if (frameId !== null) {
          window.cancelAnimationFrame(frameId);
          frameId = null;
        }
        if (dimTimer !== undefined) {
          window.clearTimeout(dimTimer);
          dimTimer = undefined;
        }
        if (sealTimer !== undefined) {
          window.clearTimeout(sealTimer);
          sealTimer = undefined;
        }
      };
      const finishReveal = () => {
        dimTimer = window.setTimeout(() => {
          dimLoserLines(elements);
          setLosersDimmed(true);
        }, CONSENSUS_CHART_TIMING.dimLosersAfterMs);
        sealTimer = window.setTimeout(() => {
          elements.win.tip.style.opacity = "0";
          setHideWinTip(true);
          setSealVisible(true);
        }, CONSENSUS_CHART_TIMING.sealAfterMs);
      };
      const play = () => {
        if (playedRef.current) {
          return;
        }
        playedRef.current = true;
        if (reducedMotion || !canAnimate) {
          jumpToFinalState(elements, setTargetLive, setLosersDimmed, setSealVisible, setHideWinTip);
          return;
        }
        resetChart(elements);
        setTargetLive(false);
        setLosersDimmed(false);
        setSealVisible(false);
        setHideWinTip(false);
        crossedTarget = false;
        const startedAt = performance.now();
        const frame = now => {
          const rawProgress = Math.min((now - startedAt) / CONSENSUS_CHART_TIMING.revealMs, 1);
          const easedProgress = easeInOutCubic(rawProgress);
          for (const key_0 of LINE_KEYS) {
            setRevealFraction(elements, key_0, easedProgress);
          }
          if (!crossedTarget && easedProgress >= CONSENSUS_CHART_TIMING.crossAt) {
            crossedTarget = true;
            setTargetLive(true);
          }
          if (rawProgress < 1) {
            frameId = window.requestAnimationFrame(frame);
            return;
          }
          finishReveal();
        };
        frameId = window.requestAnimationFrame(frame);
      };
      if (orchestrationActive) {
        if (!playbackEnabled) {
          jumpToFinalState(elements, setTargetLive, setLosersDimmed, setSealVisible, setHideWinTip);
          return cleanupPlayback;
        }
        const startTimer = window.setTimeout(() => {
          play();
        }, 0);
        return () => {
          window.clearTimeout(startTimer);
          cleanupPlayback();
        };
      }
      if (typeof IntersectionObserver === "undefined") {
        const startTimer_0 = window.setTimeout(() => {
          play();
        }, 0);
        return () => {
          window.clearTimeout(startTimer_0);
          cleanupPlayback();
        };
      }
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target === root && entry.isIntersecting) {
            play();
          }
        }
      }, {
        threshold: CONSENSUS_CHART_TIMING.intersectionThreshold
      });
      observer.observe(root);
      return () => {
        observer.disconnect();
        cleanupPlayback();
      };
    };
    $[0] = orchestrationActive;
    $[1] = playbackEnabled;
    $[2] = rootRef;
    $[3] = svgRef;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== orchestrationActive || $[6] !== playbackEnabled || $[7] !== playbackGeneration) {
    t1 = [playbackEnabled, orchestrationActive, playbackGeneration];
    $[5] = orchestrationActive;
    $[6] = playbackEnabled;
    $[7] = playbackGeneration;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  useEffect(t0, t1);
  if (orchestrationActive && !shouldPlay) {
    let t2;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        targetLive: true,
        losersDimmed: true,
        sealVisible: true,
        hideWinTip: true
      };
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t2;
  if ($[10] !== hideWinTip || $[11] !== losersDimmed || $[12] !== sealVisible || $[13] !== targetLive) {
    t2 = {
      targetLive,
      losersDimmed,
      sealVisible,
      hideWinTip
    };
    $[10] = hideWinTip;
    $[11] = losersDimmed;
    $[12] = sealVisible;
    $[13] = targetLive;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
export function ConsensusBuildsChart() {
  const $ = _c(2);
  const instanceKey = useHowItWorksPlaybackInstanceKey(1);
  let t0;
  if ($[0] !== instanceKey) {
    t0 = <ConsensusBuildsChartBody key={instanceKey} />;
    $[0] = instanceKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function ConsensusBuildsChartBody() {
  const $ = _c(18);
  const rootRef = useRef(null);
  const svgRef = useRef(null);
  const {
    targetLive,
    losersDimmed,
    sealVisible,
    hideWinTip
  } = useConsensusChartPlayback(rootRef, svgRef);
  const sealCenterY = CONSENSUS_CHART_SHAPES.win[CONSENSUS_CHART_SHAPES.win.length - 1] ?? 150;
  const targetLabelX = CONSENSUS_CHART_X_MAX + 4;
  const t0 = targetLive && "consensus-chart-target-live";
  const t1 = losersDimmed && "consensus-chart-losers-dimmed";
  const t2 = sealVisible && "consensus-chart-seal-visible";
  const t3 = hideWinTip && "consensus-chart-hide-win-tip";
  let t4;
  if ($[0] !== t0 || $[1] !== t1 || $[2] !== t2 || $[3] !== t3) {
    t4 = cn("consensus-chart relative h-full w-full", t0, t1, t2, t3);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div aria-hidden={true} className="consensus-chart-dot-grid pointer-events-none absolute inset-0 opacity-30" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <line className="consensus-chart-target-line" x1="70" x2={CONSENSUS_CHART_X_MAX} y1={CONSENSUS_CHART_TARGET_Y} y2={CONSENSUS_CHART_TARGET_Y} />;
    t7 = <text className="consensus-chart-target-label" x={targetLabelX} y={CONSENSUS_CHART_TARGET_Y + 5}>Target: 60%</text>;
    t8 = <path className="consensus-chart-line consensus-chart-line-spike" d={LINE_PATHS.spike} id="consensus-line-spike" />;
    t9 = <path className="consensus-chart-line consensus-chart-line-mid" d={LINE_PATHS.mid} id="consensus-line-mid" />;
    t10 = <path className="consensus-chart-line consensus-chart-line-win" d={LINE_PATHS.win} id="consensus-line-win" />;
    t11 = <circle className="consensus-chart-tip consensus-chart-tip-spike" id="consensus-tip-spike" r="7" />;
    t12 = <circle className="consensus-chart-tip consensus-chart-tip-mid" id="consensus-tip-mid" r="7" />;
    t13 = <circle className="consensus-chart-tip consensus-chart-tip-win" id="consensus-tip-win" r="7" />;
    $[6] = t10;
    $[7] = t11;
    $[8] = t12;
    $[9] = t13;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t10 = $[6];
    t11 = $[7];
    t12 = $[8];
    t13 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <path className="consensus-chart-seal-body" d={buildSealPath(CONSENSUS_CHART_X_MAX, sealCenterY)} />;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="consensus-chart-stage">{t5}<svg ref={svgRef} aria-hidden="true" className="relative block h-full w-full" preserveAspectRatio="xMinYMid meet" viewBox={`70 0 ${CONSENSUS_CHART_VIEW_WIDTH} 480`}>{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}<g className="consensus-chart-seal">{t14}<path className="consensus-chart-seal-check" d={buildSealCheckPath(CONSENSUS_CHART_X_MAX, sealCenterY)} /></g></svg></div>;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== t4) {
    t16 = <div ref={rootRef} className={t4} data-testid="consensus-builds-chart">{t15}</div>;
    $[16] = t4;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  return t16;
}